import type {
  GetStockLedgerParams,
  InventoryRow,
  ReservationHolder,
  StockLedgerFilters,
  StockLedgerOrigin,
  StockLedgerRow,
} from '@/infrastructure/FulfillmentAdminClient';
import { fulfillmentSourceLabel } from '@/utils/fulfillmentSource';
import { instantMs } from '@/utils/fulfillmentTime';
import type {
  ExportStockStatusResponse,
  ProductLookupResponse,
  StockEventRowResponse,
  StockStatisticResponse,
} from '@/infrastructure/StockClient';

/** The wireframe's four sub-tabs; Current Stocks is the landing tab. */
export type InventoryTab = 'current' | 'history' | 'inbound' | 'outbound';

/** Filter chips over the Stock History events. */
export type HistoryFilter = 'all' | 'confirmed' | 'pending';

/** Browsing defaults to Available descending; Total is optional and audits always walk by location. */
export type SortMode = 'available-desc' | 'total-desc' | 'total-asc' | 'location-asc' | 'location-desc';

export const MISSING = '–';

/** Catalog columns stay optional: a sku the catalog has never heard of must still show its quantity. */
export interface InventoryScreenRow extends InventoryRow {
  productId?: number;
  name?: string;
  nameKor?: string;
  brand?: string;
  size?: string;
  barcode?: string;
  imageUrl?: string;
  /** The product's storefront page, opened from the row so its size and spec are one click away (FC request 15, SSD-7569). */
  productLink?: string;
}

/** One counted line as the audit tally reads it. */
export interface AuditEntry {
  diff: number;
  isNewAddition: boolean;
}

export interface AuditTally {
  adjustments: number;
  decreases: number;
  increases: number;
  newAdditions: number;
}

/** The only four readings a decrease can have — anything else belongs to a route that already exists. */
export const ADJUST_REASONS = ['Damaged', 'Expired', 'Lost', 'Miscount'] as const;

export type AdjustReason = (typeof ADJUST_REASONS)[number];

/**
 * Only our own storefront becomes a link out, since the row says it opens skin-seoul.com: an https address on
 * skin-seoul.com or a subdomain of it. A blank, relative or foreign value in the catalog draws no link.
 */
export const storefrontHref = (link: string | undefined): string | null => {
  const trimmed = (link ?? '').trim();
  try {
    const url = new URL(trimmed);
    const host = url.hostname.toLowerCase();
    return url.protocol === 'https:' && (host === 'skin-seoul.com' || host.endsWith('.skin-seoul.com'))
      ? url.href
      : null;
  } catch {
    return null;
  }
};

export const mergeProductInfo = (rows: InventoryRow[], products: ProductLookupResponse[]): InventoryScreenRow[] => {
  const bySku = new Map(products.map((entry) => [entry.sku, entry]));
  return rows.map((row) => {
    const found = bySku.get(row.sku);
    if (!found) {
      return { ...row };
    }
    return {
      ...row,
      productId: found.productId,
      name: found.name,
      ...(found.nameKor !== undefined ? { nameKor: found.nameKor } : {}),
      ...(found.brand !== undefined ? { brand: found.brand } : {}),
      ...(found.size !== undefined ? { size: found.size } : {}),
      ...(found.barcode !== undefined ? { barcode: found.barcode } : {}),
      ...(found.productImageUrls?.[0] !== undefined ? { imageUrl: found.productImageUrls[0] } : {}),
      ...(storefrontHref(found.productLink) !== null ? { productLink: storefrontHref(found.productLink)! } : {}),
    };
  });
};

/**
 * The bucket stock sits in when it has no shelf (SSD-7213): V1's single warehouse, which the cutover left
 * the unshelved stock in and which a cleared location cell moves a row back to. It is real, reservable
 * stock, so it is shown as "Unassigned" rather than as a shelf code nobody can walk to.
 */
export const UNASSIGNED_SHELF = 'WH#00';

export const isUnassignedShelf = (locationCode: string | null | undefined): boolean =>
  !locationCode || locationCode === UNASSIGNED_SHELF;

/** The shelf as the screen names it: the code, or "Unassigned" for stock on none. */
export const shelfLabel = (locationCode: string | null | undefined): string =>
  isUnassignedShelf(locationCode) ? 'Unassigned' : (locationCode as string);

/** What the location cell holds: the code, or an empty cell whose placeholder reads "Unassigned". */
export const shelfInputValue = (locationCode: string): string => (isUnassignedShelf(locationCode) ? '' : locationCode);

/** A shelf reads `A-02-13`; a code carrying no line segment is its own line rather than being dropped. */
export const lineOf = (locationCode: string): string => {
  const [line] = locationCode.split('-');
  return line || locationCode;
};

/** The line list is derived from the locations actually registered, never from a hard-coded A/B/C; unassigned stock has its own entry. */
export const locationLines = (rows: InventoryScreenRow[]): string[] =>
  [
    ...new Set(
      rows
        .filter((row) => !isUnassignedShelf(row.locationCode))
        .map((row) => lineOf(row.locationCode))
        .filter(Boolean),
    ),
  ].sort((a, b) => a.localeCompare(b));

export const matchesQuery = (row: InventoryScreenRow, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return [row.sku, row.name, row.nameKor, row.brand]
    .filter((field): field is string => Boolean(field))
    .some((field) => field.toLowerCase().includes(needle));
};

/**
 * The line filter's value for stock with no location; no shelf code can spell it. It is always offered,
 * so finding no unassigned stock is an answer rather than a missing choice.
 */
export const UNASSIGNED_LINE = '__unassigned__';

export const inLine = (row: InventoryScreenRow, line: string): boolean => {
  if (line === UNASSIGNED_LINE) {
    return isUnassignedShelf(row.locationCode);
  }
  return line === 'all' || lineOf(row.locationCode) === line;
};

/** Stored route enums stay on the wire; the screen presents their operator-facing labels. */
export const routeLabel = (route: string): string => fulfillmentSourceLabel(route);

/** Only the routes the listed stock actually came from — a filter offering an empty route is a dead end. */
export const sourcingRouteOptions = (rows: InventoryScreenRow[]): string[] =>
  [...new Set(rows.flatMap((row) => row.sourcingRoutes ?? []))].sort((a, b) => a.localeCompare(b));

/** A shelf fed by two routes belongs under both, so narrowing to one route can never lose it. */
export const inRoute = (row: InventoryScreenRow, route: string): boolean =>
  route === 'all' || (row.sourcingRoutes ?? []).includes(route);

/** The route column: every route spelled out, or a dash — an unsourced shelf is not a blank cell. */
export const routesText = (row: InventoryScreenRow): string =>
  row.sourcingRoutes?.length ? row.sourcingRoutes.map(routeLabel).join(' · ') : MISSING;

/**
 * An unassigned shelf has no place on the audit walk, so it sorts after every assigned one whichever way the
 * walk runs. Shelf numbers compare as numbers, so A-2 comes before A-10 the way the aisle is actually walked.
 */
const byLocation = (a: InventoryScreenRow, b: InventoryScreenRow, descending = false): number => {
  if (!a.locationCode || !b.locationCode) {
    return a.locationCode ? -1 : b.locationCode ? 1 : 0;
  }
  const order = a.locationCode.localeCompare(b.locationCode, undefined, { numeric: true, sensitivity: 'base' });
  return descending ? -order : order;
};

export const sortRows = (rows: InventoryScreenRow[], mode: SortMode): InventoryScreenRow[] =>
  [...rows].sort((a, b) => {
    if (mode === 'total-desc' || mode === 'total-asc') {
      const totalDifference = mode === 'total-desc' ? b.onHand - a.onHand : a.onHand - b.onHand;
      return totalDifference || a.sku.localeCompare(b.sku);
    }
    if (mode === 'available-desc') {
      return b.available - a.available || a.sku.localeCompare(b.sku);
    }
    return byLocation(a, b, mode === 'location-desc') || a.sku.localeCompare(b.sku);
  });

/**
 * The most units one sku can be counted at on one shelf. The server only asks for a non-negative
 * integer, so this is the screen's own ceiling: far above anything a shelf holds, and well below a
 * barcode (8-13 digits) scanned into the quantity box by mistake.
 */
export const MAX_COUNT = 99_999;

export const COUNT_ERROR = `Enter a whole number from 0 to ${MAX_COUNT.toLocaleString('en-US')}.`;

/** Whether typed text is a count the screen can record: a plain whole number from 0 to MAX_COUNT. */
export const isValidCount = (value: string): boolean => {
  const text = value.trim();
  // A shelf count is typed by hand: Number() would read 0x10 as sixteen and 1e3 as a thousand.
  return /^\d+$/.test(text) && Number(text) <= MAX_COUNT;
};

/** Why a typed count cannot be recorded; nothing for a valid one or a blank box, which is simply uncounted. */
export const countInputError = (value: string): string | null =>
  value.trim() === '' || isValidCount(value) ? null : COUNT_ERROR;

/** Null while the shelf is uncounted — a blank box is not a count of zero — or when the count is not valid. */
export const countedDiff = (systemQty: number, counted: string): number | null =>
  isValidCount(counted) ? Number(counted.trim()) - systemQty : null;

export const auditTally = (entries: AuditEntry[]): AuditTally =>
  entries.reduce<AuditTally>(
    (tally, entry) => {
      if (entry.isNewAddition) {
        return { ...tally, newAdditions: tally.newAdditions + 1 };
      }
      if (entry.diff === 0) {
        return tally;
      }
      return {
        ...tally,
        adjustments: tally.adjustments + 1,
        decreases: tally.decreases + (entry.diff < 0 ? 1 : 0),
        increases: tally.increases + (entry.diff > 0 ? 1 : 0),
      };
    },
    { adjustments: 0, decreases: 0, increases: 0, newAdditions: 0 },
  );

/** Only a decrease is ambiguous; an increase can only ever be a miscount, so it is not worth asking. */
export const reasonRequiredFor = (delta: number): boolean => delta < 0;

/** The reason a ✎ adjustment records: the one chosen for a decrease, and Miscount for an increase. */
export const adjustReasonFor = (delta: number, chosen: AdjustReason | null): AdjustReason | null =>
  chosen ?? (delta > 0 ? 'Miscount' : null);

export const filterHistoryEvents = <Row extends { status: StockEventRowResponse['status'] }>(
  events: Row[],
  filter: HistoryFilter,
): Row[] => {
  if (filter === 'all') {
    return events;
  }
  const wanted = filter === 'confirmed' ? 'CONFIRMED' : 'PENDING';
  return events.filter((row) => row.status === wanted);
};

/** Row identity is (sku, location) — the same sku on two shelves is two rows. */
export const keyOf = (row: Pick<InventoryScreenRow, 'sku' | 'locationCode'>): string =>
  `${row.sku}@${row.locationCode}`;

/**
 * The rows in the order they were last shown, so a ✎ save does not send its row to a new place before the operator
 * has read it [SSD-7449]. A row that was not on screen then goes after them, in its sorted place.
 */
export const keepOrder = (sorted: InventoryScreenRow[], shown: string[]): InventoryScreenRow[] => {
  const place = new Map(shown.map((key, index) => [key, index]));
  const kept = sorted.filter((row) => place.has(keyOf(row)));
  kept.sort((a, b) => place.get(keyOf(a))! - place.get(keyOf(b))!);
  return [...kept, ...sorted.filter((row) => !place.has(keyOf(row)))];
};

export const availableOf = (statistic: StockStatisticResponse): number =>
  statistic.totalQuantity - statistic.totalReservedQuantity;

/**
 * Reserved beyond stock. The server's own readings win where it sends them; a virtual shelf floors
 * `available` at 0, so the raw counts are still checked on a server that reports neither [E-62].
 */
export const isOversold = (row: InventoryScreenRow): boolean =>
  row.reservedExceedsTotal === true || row.negativeAvailable === true || row.available < 0 || row.onHand < row.reserved;

const reservedExceedsStock = (row: InventoryScreenRow): boolean =>
  row.reservedExceedsTotal === true || row.onHand < row.reserved;

/** Out of balance although the total covers its reservations, which a count adjustment cannot repair. */
export const needsReconciliation = (row: InventoryScreenRow): boolean => isOversold(row) && !reservedExceedsStock(row);

export interface StockIntegrityIssueCounts {
  reservedExceedsStock: number;
  negativeAvailableOnly: number;
}

export const stockIntegrityIssueCounts = (rows: InventoryScreenRow[]): StockIntegrityIssueCounts => ({
  reservedExceedsStock: rows.filter(reservedExceedsStock).length,
  negativeAvailableOnly: rows.filter(needsReconciliation).length,
});

const countLabel = (count: number): string => `${count} ${count === 1 ? 'row' : 'rows'}`;

export const stockIntegritySummary = (rows: InventoryScreenRow[], everyRow = false): string => {
  const { reservedExceedsStock, negativeAvailableOnly } = stockIntegrityIssueCounts(rows);
  const scope = (count: number): string =>
    everyRow && count === rows.length ? 'every row in this list' : countLabel(count);
  return [
    reservedExceedsStock > 0 && `reserved exceeds stock on ${scope(reservedExceedsStock)}`,
    negativeAvailableOnly > 0 && `available is negative on ${scope(negativeAvailableOnly)}`,
  ]
    .filter(Boolean)
    .join(' and ');
};

/** A search as a shelf code would read it. "Multi 1" stays apart from the shelf MULTI-1: it may be a product's name. */
export const searchedCode = (query: string): string => query.trim().toUpperCase();

/** One spelling of a shelf: `b-01-01` is B-01-01 [D-c], and "Multi 1" is MULTI-1 since the server refuses spaces [SSD-7817]. */
export const normalizeLocationCode = (text: string): string =>
  text
    .trim()
    .toUpperCase()
    .replace(/^([A-Z]+)\s+(\d+)$/, '$1-$2');

/** The shelf vocabulary [D-c]: a code opens on a letter or a digit and carries no spaces or punctuation. */
const LOCATION_CODE = /^[A-Z0-9][A-Z0-9#-]{0,31}$/;

export const LOCATION_CODE_ERROR =
  'A shelf code is letters, digits, - and # only, up to 32 characters — no spaces (e.g. B-01-01, WH#00).';

/** A location import names a shelf per row: an empty one is a gap in the file, not a request to unassign the stock [SSD-7213]. */
export const LOCATION_REQUIRED_ERROR = 'A shelf location is required.';

/** Answers on a code already normalised; the message is the screen's to show, so this only judges. */
export const isLocationCode = (code: string): boolean => LOCATION_CODE.test(code);

/** Seam: an oversold shelf is a book defect, and the screen has to name which skus carry it [E-62]. */
export const oversoldSkus = (rows: InventoryScreenRow[]): string[] => [
  ...new Set(rows.filter(isOversold).map((row) => row.sku)),
];

/**
 * The row as a recorded count leaves it. The server's integrity readings describe the quantity it
 * sent, so they are worked out again here: left as they were, a repaired row would go on reading as
 * oversold and stay out of the next audit.
 */
export const withOnHand = (row: InventoryScreenRow, onHand: number): InventoryScreenRow => {
  // A shelf nothing is picked from offers nothing, however much it holds.
  const available = row.pickable === false ? 0 : onHand - row.reserved;
  return {
    ...row,
    onHand,
    available,
    ...(row.reservedExceedsTotal !== undefined ? { reservedExceedsTotal: row.reserved > onHand } : {}),
    ...(row.negativeAvailable !== undefined ? { negativeAvailable: available < 0 } : {}),
  };
};

const STOCK_CSV_HEADERS = [
  'Product ID',
  'SKU',
  'Total Quantity',
  'Total Reserved Quantity',
  'Available Quantity',
  'Locations',
  'Updated At',
];

// A SKU or a location code may carry a comma, and an unquoted one silently shifts every later column.
const csvCell = (value: string | number): string => {
  const text = String(value ?? '');
  // A cell opening with =, +, - or @ is executed as a formula by Excel and Sheets; the apostrophe defuses it.
  const safe = typeof value === 'string' && /^\s*[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

const AUDIT_CSV_HEADERS = ['SKU', 'Product', 'Location', 'System Qty', 'Counted Qty', 'Diff'];

/**
 * The walk as it stands, for the export an audit is allowed mid-count (SSD-7212): the rows the filter shows,
 * each with its book value, what was counted and the gap. An uncounted row exports a blank count and a blank
 * diff, which is not a count of zero; an invalid count exports as typed with no diff, so the sheet shows it too.
 */
export const auditCsv = (rows: InventoryScreenRow[], counted: Record<string, string>): string | null => {
  if (rows.length === 0) {
    return null;
  }
  const lines = rows.map((row) => {
    const typed = counted[keyOf(row)] ?? '';
    const diff = countedDiff(row.onHand, typed);
    return [row.sku, row.name ?? '', shelfLabel(row.locationCode), row.onHand, typed.trim(), diff === null ? '' : diff]
      .map(csvCell)
      .join(',');
  });
  return [AUDIT_CSV_HEADERS.join(','), ...lines].join('\n');
};

/** The stock export endpoint answers JSON, so the screen owns the CSV it names the download after. */
export const stockStatusCsv = (payload: ExportStockStatusResponse): string | null => {
  const stocks = payload?.stocks ?? [];
  if (stocks.length === 0) {
    return null;
  }

  const rows = stocks.map((stock) => {
    const onHand = stock.totalQuantity ?? 0;
    const reserved = stock.totalReservedQuantity ?? 0;
    const locations = Object.entries(stock.locationQuantities ?? {})
      .map(([code, qty]) => `${code}:${qty}`)
      .join('; ');

    return [
      stock.productId ?? '',
      stock.sku ?? '',
      onHand,
      reserved,
      onHand - reserved,
      locations,
      stock.updatedAt ?? '',
    ]
      .map(csvCell)
      .join(',');
  });

  return [STOCK_CSV_HEADERS.join(','), ...rows].join('\n');
};

/** A history line the pane can render, from either store. The V2 ledger carries movement types V1 cannot name. */
export interface HistoryRow extends Omit<StockEventRowResponse, 'type'> {
  type: string;
  /** Signed on ledger rows; V1 rows carry the sign in their type. */
  delta?: number;
  /** Set on ledger rows, which name their own sku because a search can answer across several [SSD-6456]. */
  sku?: string;
  productName?: string | null;
  productNameKr?: string | null;
  /** Ledger-only fields; a V1 row carries none of them. */
  origin?: string | null;
  reason?: string | null;
  onHandBefore?: number | null;
  onHandAfter?: number | null;
  sourcingRoute?: string | null;
}

// The ledger mirrors every V1 movement under these refs, and the V1 history already carries those lines.
const V1_MIRROR_REF_TYPES = ['V1_STOCK', 'DUAL_RUN_STOCK'];

// The V1 store only ever held these two types, so any other type is the ledger's alone.
const V1_MOVEMENT_TYPES = ['INBOUND', 'OUTBOUND'];

/**
 * Newest first by the instant itself: the two stores spell their stamps differently, and comparing the text
 * ordered ledger rows by weekday name above every V1 row [SSD-6941]. A stamp that names no instant sinks to
 * the bottom, and equal instants keep the order they came in.
 */
export const byNewestFirst = (a: HistoryRow, b: HistoryRow): number => {
  const newer = instantMs(b.createdAt);
  const older = instantMs(a.createdAt);
  if (newer === null || older === null) {
    return newer === older ? 0 : newer === null ? -1 : 1;
  }
  return newer - older;
};

const asHistoryRow = (row: StockLedgerRow): HistoryRow => ({
  movementId: `${row.movementType}#${row.refId ?? ''}#${row.occurredAt}`,
  type: row.movementType,
  qty: Math.abs(row.delta),
  delta: row.delta,
  status: 'CONFIRMED',
  createdAt: row.occurredAt,
  locationId: row.locationCode,
  auditor: row.actor ?? MISSING,
  ...(row.trackingNumber ? { trackingNumber: row.trackingNumber } : {}),
  sku: row.sku,
  productName: row.productName,
  productNameKr: row.productNameKr,
  ...(row.refType === 'ORDER' && row.refId ? { orderId: row.refId } : {}),
  origin: row.origin ?? null,
  reason: row.reason ?? null,
  onHandBefore: row.onHandBefore ?? null,
  onHandAfter: row.onHandAfter ?? null,
  sourcingRoute: row.sourcingRoute ?? null,
});

export const mergeLedgerMovements = (events: StockEventRowResponse[], rows: StockLedgerRow[]): HistoryRow[] => {
  const ledgerRows = rows.filter((row) => !V1_MIRROR_REF_TYPES.includes(row.refType ?? '')).map(asHistoryRow);

  return [...events, ...ledgerRows].sort(byNewestFirst);
};

/**
 * The ledger read on its own, for the four non-SKU keys V1 cannot answer. Nothing mirrors these rows here,
 * so the V1 mirrors are kept: dropping them would leave an order or a waybill with no movements at all.
 */
export const ledgerMovements = (rows: StockLedgerRow[]): HistoryRow[] => rows.map(asHistoryRow).sort(byNewestFirst);

/** Stands in for a value the row was booked without; it must never read as zero. */
export const NOT_RECORDED = 'Not recorded';

const ORIGIN_LABELS: Readonly<Record<StockLedgerOrigin, string>> = {
  MANUAL_ADJUST: 'Manual adjust',
  STOCK_AUDIT: 'Stock audit',
  RESERVATION_RELEASE: 'Reservation release',
  LAUNCH_OPENING_BALANCE: 'Launch opening balance',
};

export const originLabel = (origin: string): string =>
  ORIGIN_LABELS[origin as StockLedgerOrigin] ?? origin.replace(/_/g, ' ');

/**
 * What booked a ledger row, beside its type. An AUDIT_ADJUST with no origin predates the column, so it
 * reads as not recorded; every other row without one simply has nothing to add.
 */
export const historyOriginText = (row: Pick<HistoryRow, 'type' | 'origin'>): string | null => {
  if (row.origin) {
    return originLabel(row.origin);
  }
  return row.type === 'AUDIT_ADJUST' ? NOT_RECORDED : null;
};

/** The recorded reason; an AUDIT_ADJUST booked before origins were recorded could not have kept one either. */
export const historyReasonText = (row: Pick<HistoryRow, 'type' | 'origin' | 'reason'>): string => {
  if (row.reason) {
    return row.reason;
  }
  return row.type === 'AUDIT_ADJUST' && !row.origin ? NOT_RECORDED : MISSING;
};

/** Before → after on the row's shelf; a row booked without balances says so rather than showing 0. */
export const shelfBalanceText = (row: Pick<HistoryRow, 'onHandBefore' | 'onHandAfter'>): string =>
  row.onHandBefore != null && row.onHandAfter != null ? `${row.onHandBefore} → ${row.onHandAfter}` : NOT_RECORDED;

/** A ledger row always carries a route (UNKNOWN when nothing routed it); a V1 row or an older server carries none. */
export const historyRouteText = (row: Pick<HistoryRow, 'sourcingRoute'>): string =>
  row.sourcingRoute ? routeLabel(row.sourcingRoute) : MISSING;

/** One choice of the Stock History event filter: an origin, or a plain movement type. */
export interface HistoryEventOption {
  value: string;
  label: string;
}

/** No event narrowing; the server is asked for every movement. */
export const ALL_EVENTS = 'all';

const ORIGIN_PREFIX = 'origin:';
const MOVEMENT_PREFIX = 'movementType:';

/** Origins first, because manual adjust and stock audit are what the type alone cannot tell apart. */
export const historyEventOptions = (filters: StockLedgerFilters | undefined): HistoryEventOption[] => [
  ...(filters?.origins ?? []).map((origin) => ({ value: `${ORIGIN_PREFIX}${origin}`, label: originLabel(origin) })),
  ...(filters?.movementTypes ?? []).map((type) => ({ value: `${MOVEMENT_PREFIX}${type}`, label: type })),
];

/** The ledger query parameters one event choice stands for. */
export const historyEventQuery = (value: string): Pick<GetStockLedgerParams, 'origin' | 'movementType'> => {
  if (value.startsWith(ORIGIN_PREFIX)) {
    return { origin: value.slice(ORIGIN_PREFIX.length) as StockLedgerOrigin };
  }
  if (value.startsWith(MOVEMENT_PREFIX)) {
    return { movementType: value.slice(MOVEMENT_PREFIX.length) };
  }
  return {};
};

/** Whether V1 can hold any event of this choice: a V1 row has no origin, and its only types are INBOUND and OUTBOUND. */
export const v1HoldsEvent = (value: string): boolean => {
  const query = historyEventQuery(value);
  return !query.origin && (!query.movementType || V1_MOVEMENT_TYPES.includes(query.movementType));
};

/** Narrows the V1 events the same way the ledger is asked to. */
export const filterV1Events = (events: StockEventRowResponse[], value: string): StockEventRowResponse[] => {
  if (!v1HoldsEvent(value)) {
    return [];
  }
  const { movementType } = historyEventQuery(value);
  return movementType ? events.filter((event) => event.type === movementType) : events;
};

/** Reserved holders split the way the Reserved card counts them: shelf holders make up its number. */
export interface HolderGroups {
  onShelf: ReservationHolder[];
  unassigned: ReservationHolder[];
}

export const groupHolders = (holders: ReservationHolder[]): HolderGroups => ({
  onShelf: holders.filter((holder) => Boolean(holder.locationCode)),
  unassigned: holders.filter((holder) => !holder.locationCode),
});

export const holderUnits = (holders: ReservationHolder[]): number =>
  holders.reduce((total, holder) => total + holder.qty, 0);
